import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { ApiError, getWhatsAppAccount } from '../services/whatsappApi';
import type { WhatsAppAccount } from '../types/whatsapp';
import { isFeatureUnavailable } from './availability';

// The WhatsApp feature is per clinic: merging to main reaches every clinic, and
// connecting a number is only possible for the pilot until Embedded Signup exists
// (phase 5). Everything WhatsApp-related, the Navbar button included, appears only
// when GET /whatsapp/account returns a connected account. Clinics outside the
// backend's WHATSAPP_DEMO list get 403 `feature_unavailable`: they see the Navbar
// button marked "Próximamente" instead.

interface WhatsAppContextValue {
  account: WhatsAppAccount | null;
  /** The account was fetched (with or without result). */
  loaded: boolean;
  /** The clinic has a WhatsApp number: show the inbox entry and run the polling. */
  enabled: boolean;
  /** The server said this clinic is not on the demo list: show "Próximamente". */
  comingSoon: boolean;
  refreshAccount: () => Promise<void>;
  /** Applies an account the server just returned (the assistant switch), without another request. */
  applyAccount: (account: WhatsAppAccount) => void;
}

const WhatsAppContext = createContext<WhatsAppContextValue>({
  account: null,
  loaded: false,
  enabled: false,
  comingSoon: false,
  refreshAccount: async () => {},
  applyAccount: () => {},
});

// Public pages never fetch the account.
const PUBLIC_PATHS = ['/login', '/signup'];

export const WhatsAppProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const isPublic = PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const [signedIn, setSignedIn] = useState<boolean>(() => auth.currentUser !== null);
  const [account, setAccount] = useState<WhatsAppAccount | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (user) => setSignedIn(user !== null)), []);

  const refreshAccount = useCallback(async () => {
    try {
      const acct = await getWhatsAppAccount();
      setUnavailable(false);
      setAccount(acct.connected ? acct : null);
    } catch (err) {
      if (isFeatureUnavailable(err)) {
        setUnavailable(true);
        setAccount(null);
        return;
      }
      // 401/402 (no session, lapsed subscription): no WhatsApp UI. Any other error
      // (network, 5xx) keeps the account already held, so a blip does not hide the inbox.
      if (err instanceof ApiError && err.isAuthFailure) setAccount(null);
      else console.warn('WhatsApp account unavailable', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  const applyAccount = useCallback((acct: WhatsAppAccount) => {
    setAccount(acct.connected ? acct : null);
  }, []);

  useEffect(() => {
    if (!signedIn) {
      setAccount(null);
      setUnavailable(false);
      setLoaded(false);
      return;
    }
    if (isPublic || loaded) return;
    void refreshAccount();
  }, [signedIn, isPublic, loaded, refreshAccount]);

  const value = useMemo<WhatsAppContextValue>(
    () => ({
      account,
      loaded,
      enabled: signedIn && account !== null,
      comingSoon: signedIn && loaded && unavailable,
      refreshAccount,
      applyAccount,
    }),
    [account, loaded, signedIn, unavailable, refreshAccount, applyAccount],
  );
  return <WhatsAppContext.Provider value={value}>{children}</WhatsAppContext.Provider>;
};

export const useWhatsApp = (): WhatsAppContextValue => useContext(WhatsAppContext);
